/**
 * Created by Denis Kuzin on 23 december 2024
 */

const FlatFieldView = cc.Node.extend({
    avoidNode: "FlatFieldView",
    ctor: function (field) {
        this._super();

        this.field = field;

        this.rotated = cleverapps.resolution.mode !== cleverapps.WideMode.HORIZONTAL;

        this.setContentSize(this.getSize());

        this.setAnchorPoint(0.5, 0.5);

        this.tiles = new cc.Node();
        this.tiles.setAnchorPoint(0.5, 0.5);
        this.addChild(this.tiles, 0);

        this.grounded = new cc.Node();
        this.grounded.setAnchorPoint(0.5, 0.5);
        this.addChild(this.grounded, 1);

        this.tilesMap = new Map();

        this.units = new cc.Node();
        this.units.setAnchorPoint(0.5, 0.5);
        this.addChild(this.units, 3);

        this.aboveUnits = new cc.Node();
        this.aboveUnits.setAnchorPoint(0.5, 0.5);
        this.addChild(this.aboveUnits, 4);

        this.selection = new cleverapps.Spine(bundles.highlight_animations.jsons.highlight_json);
        this.aboveUnits.addChild(this.selection);

        const animations = this.animations = this.cloudsAbove = this.movingNode = new cc.Node();
        animations.setContentSize(this.getContentSize());
        animations.setAlignment(this.width / 2, this.height / 2);
        animations.setAnchorPoint(0.5, 0.5);
        animations.debugId = "Animations";
        animations._debugExclude = true;
        this.addChild(animations, 10);

        this.field.onAddUnit = this.createListener(this.createObjectView.bind(this, FlatFieldView.LAYER_UNITS));
        this.field.onDiscoverMapView = this.createListener(() => this);

        this.updateAnimations();
        this.updateTilesLayer();
        this.updateUnits();

        this.addControls();
        this.onResize();

        this.field.on("unitSelectionChanged", this.toggleSelection.bind(this));

        cleverapps.aims.registerTarget("units", this, {
            noTargetDelta: true,
            flyingUnderShadow: true,
            flyingAnimation: Reward.SPAWN_UNITS_ANIMATION
        });
    },

    onResize: function () {
        const needRotateMap = cleverapps.resolution.mode !== cleverapps.WideMode.HORIZONTAL;

        if (needRotateMap !== this.isRotated() && Merge2.current) {
            this.rotate();
        }
    },

    getTileView: function (x, y) {
        return this.tilesMap.get(`${x}_${y}`);
    },

    updateTilesLayer: function () {
        const size = this.getSize();

        this.tiles.removeAllChildren();
        this.tiles.setContentSize(size);
        this.tiles.setAlignment(this.width / 2, this.height / 2);
        this.tilesMap.clear();

        this.field.iterate((x, y) => {
            this.createObjectView(FlatFieldView.LAYER_TILES, x, y, { ground: true });
        });

        let convertFromField, convertToField;

        if (this.isRotated()) {
            convertFromField = (x, y) => cc.p(y, x);
            convertToField = (cell) => cc.p(cell.y, cell.x);
        } else {
            convertFromField = (x, y) => cc.p(x, this.height - y);
            convertToField = (cell) => cc.p(cell.x, this.height - cell.y);
        }

        const area = [];

        this.field.iterate((x, y) => {
            area.push(convertFromField(x, y));
        });

        BorderRenderer.place(
            area,
            (borders, cell) => {
                if (borders.length <= 0) {
                    return;
                }

                cell = convertToField(cell);

                borders.forEach((border) => {
                    const styles = cleverapps.styles.FlatFieldView;

                    const imageName = border.name;
                    const borderPadding = styles.borderPadding[imageName];
                    const padding = borderPadding !== undefined ? borderPadding : styles.borderPadding.default;

                    let dx = 0;
                    let dy = 0;

                    if (imageName.includes("right")) {
                        dx = 1;
                    } else if (imageName.includes("left")) {
                        dx = -1;
                    }

                    if (imageName.includes("up")) {
                        dy = 1;
                    } else if (imageName.includes("down")) {
                        dy = -1;
                    }

                    dx *= (styles.cell.width / 2 + padding.x);
                    dy *= (styles.cell.height / 2 + padding.y);

                    this.createObjectView(FlatFieldView.LAYER_TILES, cell.x, cell.y, { decorator: `border_${imageName}`, dx, dy });
                }, this);
            }
        );
    },

    updateUnits: function () {
        const size = this.getSize();

        this.units.setContentSize(size);
        this.units.setAlignment(this.width / 2, this.height / 2);

        this.field.iterateUnits((x, y, unit) => {
            unit.onRemove();
            if (unit.waitingSpawn) {
                return;
            }
            const layer = unit.isGrounded() ? FlatFieldView.LAYER_GROUNDED : FlatFieldView.LAYER_UNITS;
            this.createObjectView(layer, x, y, unit);
        });
    },

    updateAnimations: function () {
        const size = this.getSize();

        this.animations.setContentSize(size);
        this.animations.setAlignment(this.width / 2, this.height / 2);
    },

    isRotated: function () {
        return this.rotated;
    },

    rotate: function () {
        this.rotated = !this.rotated;

        const size = this.getSize();

        this.setContentSize(size);

        if (this.field.dragging) {
            this.field.dragging.onRemove();
            this.field.stopDragging();
        }

        this.updateAnimations();
        this.updateTilesLayer();
        this.updateUnits();
        this.updateSelection();
    },

    updateSelection: function () {
        const unit = this.field.selectedUnit;

        if (unit && this.selection.isVisible()) {
            this.alignViewInGrid(unit.x, unit.y, this.selection);
        }
    },

    getSize: function () {
        const styles = cleverapps.styles.FlatFieldView;
        const totalWidth = this.field.width * styles.cell.width;
        const totalHeight = this.field.height * styles.cell.height;

        return this.isRotated()
            ? cc.size(totalHeight + styles.padding.left + styles.padding.right, totalWidth + styles.padding.bottom + styles.padding.top)
            : cc.size(totalWidth + styles.padding.left + styles.padding.right, totalHeight + styles.padding.bottom + styles.padding.top);
    },

    getCellByTouch: function (touch) {
        const pos = this.convertTouchToNodeSpace(touch);
        return this.getCellByCoordinates(pos);
    },

    getCellByCoordinates: function (p, rawValue) {
        const styles = cleverapps.styles.FlatFieldView;
        const zero = this.alignInGrid(0, 0);

        let x, y;

        if (!this.isRotated()) {
            x = (p.x - zero.x) / styles.cell.width;
            y = (p.y - zero.y) / styles.cell.height;
        } else {
            x = (zero.y - p.y) / styles.cell.height;
            y = (p.x - zero.x) / styles.cell.width;
        }

        p = rawValue ? cc.p(x, y) : cc.p(Math.round(x), Math.round(y));

        return p;
    },

    createObjectView: function (layer, x, y, object) {
        if (!object) {
            cleverapps.throwAsync(`createObjectView with no object ${layer} ${x} ${y}`);
            return;
        }

        let view;

        if (object instanceof Unit) {
            view = new UnitView(object);
            view.sprite._debugExclude = true;
        } else if (object.ground) {
            view = this.createGroundView(x, y);
        } else if (object.decorator) {
            view = this.createDecoratorView(x, y, object);
        } else {
            cleverapps.throwAsync(`createObjectView with unknown object ${layer} ${x} ${y}`);
            return;
        }

        if (!view) {
            return;
        }

        this.addObjectView(layer, x, y, view);
    },

    addObjectView: function (layer, x, y, view) {
        let parent;

        if (layer === FlatFieldView.LAYER_UNITS) {
            parent = this.units;
        } else if (layer === FlatFieldView.LAYER_GROUNDED) {
            parent = this.grounded;
        } else if (layer === FlatFieldView.LAYER_TILES) {
            parent = this.tiles;
        } else {
            cleverapps.throwAsync(`addObjectView with unknown layer ${layer} ${x} ${y}`);
            return;
        }

        if (view.ground) {
            this.tilesMap.set(`${x}_${y}`, view);
        }

        if (view.parent) {
            view.replaceParentSamePlace(parent);
        } else {
            parent.addChild(view);
        }

        const cellPosition = this.alignInGrid(x, y);

        view.setAlignment(cellPosition.x + (view.dx || 0), cellPosition.y + (view.dy || 0));
    },

    createGroundView: function (x, y) {
        const bundle = bundles.tiles_garden;
        const isOdd = (x + y) % 2;

        const groundView = new cc.Sprite(isOdd ? bundle.frames.ground_1 : bundle.frames.ground_2);
        groundView.ground = true;

        return groundView;
    },

    createDecoratorView: function (x, y, object) {
        const bundle = bundles.decorators_garden;
        const json = bundle && bundle.jsons[object.decorator];
        const frame = bundle && bundle.frames[object.decorator];

        let image;

        if (json) {
            image = new cleverapps.Spine(json);
            image.setAnimation(0, "animation", true);
        } else if (frame) {
            image = new cc.Sprite(frame);
        }

        if (!image) {
            cleverapps.throwAsync(`Not found resources for decorator ${object.decorator}`);
            return;
        }

        image.dx = object.dx || 0;
        image.dy = object.dy || 0;
        return image;
    },

    toggleSelection: function (unit, isDragging) {
        if (unit && !isDragging) {
            this.alignViewInGrid(unit.x, unit.y, this.selection);
            this.selection.setVisible(true);

            this.selection.setAnimationAndIdleAfter("show_up", "animation");
            return;
        }

        this.selection.setVisible(false);
    },

    toScreen: function (x, y) {
        const styles = cleverapps.styles.FlatFieldView;

        const cell = styles.cell;
        const padding = styles.padding;

        if (this.isRotated()) {
            return cc.p(padding.left + y * cell.height, padding.bottom + (this.field.width - x) * cell.width);
        }
        return cc.p(padding.left + x * cell.width, padding.bottom + y * cell.height);
    },

    alignInGrid: function (x, y) {
        return this.toScreen(x + 0.5, y + 0.5);
    },

    alignViewInGrid: function (x, y, view) {
        const cellPosition = this.alignInGrid(x, y);
        view.setAlignment(cellPosition.x, cellPosition.y);
    },

    alignPositionInGrid: function (x, y, position) {
        const cellPosition = this.alignInGrid(x, y);
        return cc.p(position.x + cellPosition.x, position.y + cellPosition.y);
    },

    getUnitCenterPos: function (x, y) {
        return this.alignInGrid(x, y);
    },

    addControls: function () {
        let dragUnit, touchUnit, baseTouch;

        const releaseUnit = (cancelled) => {
            const unitView = dragUnit && dragUnit.onGetView();

            touchUnit = undefined;
            dragUnit = undefined;
            baseTouch = undefined;

            this.field.setDragging(undefined);

            if (!unitView) {
                return;
            }

            const dropTarget = this.findDropTarget(unitView);
            unitView.handleDragEnd(cancelled, dropTarget);
        };

        const pointerHandler = cleverapps.pointerManager.create(this, {
            checkFocus: (touch) => {
                let unit = baseTouch === touch && touchUnit;
                if (!unit) {
                    const cell = this.getCellByTouch(touch);
                    unit = cell && this.field.getUnit(cell.x, cell.y);
                }

                const unitView = unit && unit.onGetView();
                return Boolean(unitView) && cleverapps.focusManager.checkEventNode(unitView);
            },

            filter: (touch) => {
                const cell = this.getCellByTouch(touch);
                if (!cell) {
                    return false;
                }

                const unit = this.field.getUnit(cell.x, cell.y);
                if (unit && unit.onGetView() && unit.onGetView().checkTouchInside(touch)) {
                    releaseUnit(true);
                    touchUnit = unit;
                    baseTouch = touch;

                    if (cleverapps.config.debugMode) {
                        console.log("click pressed", {
                            code: unit.code,
                            stage: unit.stage,
                            x: unit.x,
                            y: unit.y,
                            name: cleverapps.unitsLibrary.getUnitName(unit)
                        });
                    }

                    return true;
                }

                if (this.field.currentSelection) {
                    return true;
                }

                const orangerySelection = this.field.orangery && this.field.orangery.selected;
                if (orangerySelection && !cleverapps.focusManager.isFocused()) {
                    return true;
                }

                return false;
            },

            onClick: (touch) => {
                const cell = this.getCellByTouch(touch);
                const orangery = this.field.orangery;
                if (orangery && !cleverapps.focusManager.isFocused() && orangery.onClick(cell)) {
                    return;
                }

                if (baseTouch === touch) {
                    if (touchUnit && touchUnit.onGetView() && touchUnit.onGetView().handleClick(touch)) {
                        releaseUnit();
                        return;
                    }

                    releaseUnit();
                }

                this.field.hideSelect();
            },

            onDragMoveNotThrottled: (touch) => {
                if (baseTouch !== touch) {
                    return;
                }

                if (dragUnit && dragUnit.onGetView()) {
                    dragUnit.onGetView().handleFollowPointer(touch);
                }
            },

            onDragStart: (touch) => {
                if (baseTouch !== touch) {
                    return;
                }

                if (touchUnit && touchUnit.onGetView() && touchUnit.onGetView().handleDragStart(touch)) {
                    dragUnit = touchUnit;
                    return true;
                }
            },

            onDragMove: (touch) => {
                if (baseTouch !== touch) {
                    return;
                }

                const unitView = dragUnit && dragUnit.onGetView();
                if (!unitView) {
                    return;
                }

                const dropTarget = this.findDropTarget(unitView);
                unitView.handleDragMove(dropTarget);
            },

            onDragEnd: (touch) => {
                if (baseTouch !== touch) {
                    return;
                }

                releaseUnit();
            }
        });

        this.field.stopDragging = this.createListener(() => {
            releaseUnit(true);
        });

        this.field.on("stop", () => {
            pointerHandler.remove();
        });
    },

    findDropTarget: function (unitView) {
        const getters = Field.DROP_TARGETS;

        for (let i = 0; i < getters.length; i++) {
            const target = getters[i]();
            if (target && target.checkDropInside(unitView)) {
                return target;
            }
        }
    }
});

FlatFieldView.LAYER_TILES = 0;
FlatFieldView.LAYER_GROUNDED = 1;
FlatFieldView.LAYER_UNITS = 2;

FlatFieldView.MIN_SMILE_DELAY = 5;
FlatFieldView.MAX_SMILE_DELAY = 10;

cleverapps.styles.FlatFieldView = {
    cell: {
        width: 148,
        height: 148
    },

    padding: {
        left: 18,
        right: 18,
        bottom: 18,
        top: 18
    },

    borderPadding: {
        default: { x: 8, y: 8 }
    }
};
